import type { StoredLotEvent } from '../db'
import type { Lot, LotEvent, LotEventHistoryScope } from '../../types/lot'

import { getOfflineDb } from '../dbFactory'

const LINEAGE_EMPTY_MARKER_PREFIX = '__lot-lineage-empty__:'

const normalizeString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

const uniqueStrings = (values: string[]): string[] => {
  const seen = new Set<string>()
  const out: string[] = []

  values.forEach((value) => {
    const normalized = normalizeString(value)
    if (!normalized || seen.has(normalized)) return
    seen.add(normalized)
    out.push(normalized)
  })

  return out
}

const toStoredLotEvent = (event: LotEvent): StoredLotEvent => {
  const sourceLotIds = uniqueStrings(event.sourceLotIds ?? [])
  const targetLotIds = uniqueStrings(event.targetLotIds ?? [])

  return {
    ...event,
    sourceLotIds,
    targetLotIds,
    lotIds: uniqueStrings([...sourceLotIds, ...targetLotIds])
  }
}

const buildLineageEmptyMarkerId = (lotId: string): string => `${LINEAGE_EMPTY_MARKER_PREFIX}${lotId}`

const buildLineageEmptyMarker = (lotId: string): StoredLotEvent => ({
  id: buildLineageEmptyMarkerId(lotId),
  eventType: 'create',
  recordedAt: '1970-01-01T00:00:00.000Z',
  sourceLotIds: [],
  targetLotIds: [],
  lotIds: [lotId],
  cacheState: 'lineage-empty'
})

const isLineageEmptyMarker = (event: StoredLotEvent): boolean => event.cacheState === 'lineage-empty'

const dedupeStoredLotEvents = (events: StoredLotEvent[]): StoredLotEvent[] => {
  const byId = new Map<string, StoredLotEvent>()
  events.forEach((event) => {
    byId.set(event.id, event)
  })
  return Array.from(byId.values())
}

const sortLotEvents = (events: LotEvent[]): LotEvent[] => {
  return [...events].sort((left, right) => {
    const recordedDelta = Date.parse(right.recordedAt) - Date.parse(left.recordedAt)
    if (Number.isFinite(recordedDelta) && recordedDelta !== 0) {
      return recordedDelta
    }

    const updatedDelta = Date.parse(right.updatedAt ?? '') - Date.parse(left.updatedAt ?? '')
    if (Number.isFinite(updatedDelta) && updatedDelta !== 0) {
      return updatedDelta
    }

    return right.id.localeCompare(left.id)
  })
}

const stripStoredFields = (event: StoredLotEvent): LotEvent => {
  const { lotIds: _lotIds, ...lotEvent } = event
  return lotEvent
}

const hasReachableLotReference = (event: StoredLotEvent, lotIds: Set<string>): boolean => {
  return event.lotIds.some((lotId) => lotIds.has(lotId))
}

export const dexieLotEventRepo = {
  async list(params: {
    lotId: string
    lineageRootId?: string
    scope?: LotEventHistoryScope
  }): Promise<LotEvent[]> {
    const lotId = normalizeString(params.lotId)
    if (!lotId) return []

    const db = getOfflineDb()
    const directEvents = await db.lotEvents.where('lotIds').equals(lotId).toArray()
    const visibleDirectEvents = directEvents.filter((event) => !isLineageEmptyMarker(event))

    if ((params.scope ?? 'lineage') !== 'lineage') {
      return sortLotEvents(visibleDirectEvents.map(stripStoredFields))
    }

    const lineageRootId = normalizeString(params.lineageRootId)
    if (!lineageRootId) {
      return sortLotEvents(visibleDirectEvents.map(stripStoredFields))
    }

    const lineageEvents = await db.lotEvents.where('lineageRootId').equals(lineageRootId).toArray()
    return sortLotEvents(
      dedupeStoredLotEvents([...visibleDirectEvents, ...lineageEvents.filter((event) => !isLineageEmptyMarker(event))])
        .map(stripStoredFields)
    )
  },

  async listForLotIds(lotIds: string[]): Promise<LotEvent[]> {
    const requestedLotIds = uniqueStrings(lotIds)
    if (requestedLotIds.length === 0) return []

    const requestedLotIdSet = new Set(requestedLotIds)
    const events = await getOfflineDb().lotEvents.toArray()

    return sortLotEvents(
      events
        .filter((event) => !isLineageEmptyMarker(event))
        .filter((event) => event.lotIds.some((lotId) => requestedLotIdSet.has(lotId)))
        .map(stripStoredFields)
    )
  },

  async upsertMany(events: LotEvent[]): Promise<void> {
    if (events.length === 0) return

    const db = getOfflineDb()
    const storedEvents = events.map((event) => toStoredLotEvent(event))
    const touchedLotIds = uniqueStrings(storedEvents.flatMap((event) => event.lotIds))

    await db.transaction('rw', db.lotEvents, async () => {
      await db.lotEvents.bulkPut(storedEvents)
      if (touchedLotIds.length > 0) {
        await db.lotEvents.bulkDelete(touchedLotIds.map((lotId) => buildLineageEmptyMarkerId(lotId)))
      }
    })
  },

  async markLineageHistoryEmpty(lotIds: string[]): Promise<void> {
    const requestedLotIds = uniqueStrings(lotIds)
    if (requestedLotIds.length === 0) return

    await getOfflineDb().lotEvents.bulkPut(requestedLotIds.map((lotId) => buildLineageEmptyMarker(lotId)))
  },

  async listCachedLotIds(lotIds: string[]): Promise<string[]> {
    const requestedLotIds = uniqueStrings(lotIds)
    if (requestedLotIds.length === 0) return []

    const remainingLotIds = new Set(requestedLotIds)
    const cachedLotIds = new Set<string>()
    const events = await getOfflineDb().lotEvents.toArray()

    events.forEach((event) => {
      event.lotIds.forEach((lotId) => {
        if (!remainingLotIds.has(lotId)) return
        cachedLotIds.add(lotId)
      })
    })

    return requestedLotIds.filter((lotId) => cachedLotIds.has(lotId))
  },

  async pruneUnreachable(lots: Pick<Lot, 'id' | 'lineageRootId'>[]): Promise<void> {
    const reachableLotIds = new Set(uniqueStrings(lots.map((lot) => lot.id)))
    const reachableLineageRootIds = new Set(uniqueStrings(
      lots.map((lot) => normalizeString(lot.lineageRootId) ?? lot.id)
    ))
    const db = getOfflineDb()
    const events = await db.lotEvents.toArray()

    const staleEventIds = events
      .filter((event) => {
        if (hasReachableLotReference(event, reachableLotIds)) {
          return false
        }

        const lineageRootId = normalizeString(event.lineageRootId)
        return !lineageRootId || !reachableLineageRootIds.has(lineageRootId)
      })
      .map((event) => event.id)

    if (staleEventIds.length === 0) return
    await db.lotEvents.bulkDelete(staleEventIds)
  }
}